import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { cx } from '@/components/ui';
import { MarketSwitch } from './MarketScopeTabs';
import { isCommonPath, isTabVisible, pathAfterScopeChange, useMarketScope } from './marketScope';
import { isCommonMarketTab } from './marketSeries';

/**
 * 단기매매 화면 사이를 오가는 하위 탭. (2026-08-19)
 *
 * 🔴 왜 최상위 탭(cm_menu)으로 만들지 않았나
 *   1. 별도 권한이 되어버린다
 *      cm_menu 에 행을 넣으면 회원×메뉴 매트릭스에 칸이 하나 더 생긴다.
 *      관리자가 그것을 끄면 추천은 보는데 모의투자는 못 보는 사람이 생긴다.
 *      모의투자는 추천의 결과를 보는 화면이라 그렇게 갈릴 이유가 없다.
 *
 *   2. 폰에서 최상위 탭이 6개가 된다
 *      AppLayout 주석이 "관리자는 이미 탭이 5개라 하나 더하면 가로 스크롤이
 *      길어진다" 고 적어둔 그 문제다.
 *
 *   여기 있는 화면은 전부 TRADING 권한 하나로 열린다. 그래서 그 안에서 가른다.
 *
 * 🔴 매매일지는 여기 없다 (2026-08-28 이동)
 *   실제 체결·평단·손익이라 본인만 봐야 한다. 이 줄의 화면들은 가족 모두에게
 *   열리는 공용 결과다. AccountNav 로 옮겼다.
 *
 * 🔴 순서가 곧 흐름이다
 *   오늘 무엇을 볼까 → 실제로 굴렸으면 어땠나 → 지금까지 맞았나.
 *   추천을 보다가 그 추천이 어떻게 됐는지로 자연스럽게 넘어가야 한다.
 */

/**
 * 🔴 주도주만 강조색이다 (2026-09-11 사용자 요청)
 *
 *   "주도주 탭은 조금 특별한 느낌이니까 색상이나 아이콘 등을 추가해서
 *    다른 탭이랑 구분되게 표시되었으면 해"
 *
 * 성격이 실제로 다르다. 나머지 다섯은 "우리 시스템이 무엇을 했나" 를 보는
 * 화면인데, 주도주는 관리자가 고른 섹터에서 지금 거래가 몰리는 곳을 보는
 * **시장 쪽 화면**이다. 모든 계정이 같은 목록을 본다는 점도 여기만 그렇다.
 *
 * 🔴 색과 아이콘을 함께 쓴다. 색만으로 구분하지 않는다
 *   색각 이상이나 흑백 인쇄에서도 갈려야 한다. 색 선택 근거와 대비 수치는
 *   index.css 의 --lead 주석에 있다.
 */
const TABS: { to: string; label: string; end: boolean; icon?: string }[] = [
  { to: '/trading', label: '추천', end: true },
  { to: '/trading/paper', label: 'AI 모의투자', end: false },
  { to: '/trading/leading', label: '주도주', end: false, icon: '🔥' },
  { to: '/trading/sectors', label: '섹터', end: false },
  { to: '/trading/alerts', label: '가격 알림', end: false },
  { to: '/trading/journal', label: '지난 추천', end: false },
  // 환율 (2026-09-19, I-61). 흐름 밖의 참고 화면이라 맨 끝이다
  // ✏️ 2026-09-29 I-81 — "환율" → "시장지표"(안에 환율 · 비트코인 · 금 · 국고채 · 지수 하위 탭 · 경로는 그대로)
  { to: '/trading/fx', label: '시장지표', end: false },
];

export function TradingNav() {
  const scope = useMarketScope((s) => s.scope);
  const { pathname, search } = useLocation();
  const navigate = useNavigate();

  return (
    <div className="flex flex-col gap-2">
      {/*
        국내 / 미국은 하위 탭 위에 둔다 (2026-09-19 사용자 결정) - MarketSwitch 주석.
        공통 화면(환율 — 가격 알림은 2026-09-28 부터 스위치를 따른다 I-83)에서도 **누를 수 있다** (2026-09-20 사용자 지시) -
        내용은 같고 아래 탭 구성만 바뀐다. 국내 전용 화면에서 미국으로 바꾸면 추천으로 옮긴다.
        ✏️ 2026-10-17 사용자 Q17 — 시장지표 안의 국고채 · 지수 하위 탭(?tab=bond · index)은 스위치를 따른다.
          그 탭에서는 "공통입니다" 문구를 끈다(isCommonMarketTab) — 환율 · 비트코인 · 금은 그대로 공통
      */}
      <MarketSwitch
        common={isCommonPath(pathname) && isCommonMarketTab(new URLSearchParams(search).get('tab'))}
        onChange={(next) => {
          const to = pathAfterScopeChange(pathname, next);
          if (to !== null) {
            navigate(to);
          }
        }}
      />
    <nav className="flex flex-wrap gap-1" aria-label="단기매매 화면">
      {/* 해외에서는 국내 전용 탭(주도주 · 섹터)을 숨긴다 - 업종 분류가 국내에만 있다 */}
      {TABS.filter((tab) => isTabVisible(tab.to, scope)).map((tab) => (
        <NavLink
          key={tab.to}
          to={tab.to}
          /*
            end 가 필요한 이유
              /trading 은 나머지 둘의 접두사다. end 가 없으면
              모의투자 화면에서도 '추천' 이 활성으로 남는다.
          */
          end={tab.end}
          className={({ isActive }) =>
            cx(
              'rounded-full px-3 py-1 text-sm transition-colors',
              tab.icon
                ? isActive
                  ? 'bg-lead text-lead-fg'
                  : 'bg-lead-weak text-lead hover:bg-lead hover:text-lead-fg'
                : isActive
                  ? 'bg-brand text-brand-fg'
                  : 'bg-surface-hover text-fg-muted hover:text-fg',
            )
          }
        >
          {/* 아이콘은 장식이다. 화면낭독기는 라벨만 읽으면 된다 */}
          {tab.icon && (
            <span aria-hidden="true" className="mr-1">
              {tab.icon}
            </span>
          )}
          {tab.label}
        </NavLink>
      ))}
    </nav>
    </div>
  );
}
